'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useRoleNavigation } from '../hooks/use-role-navigation';

interface RouteGuardProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  fallback?: React.ReactNode;
}

/**
 * Componente para proteger rutas basado en roles
 */
export function RouteGuard({ children, allowedRoles, fallback }: RouteGuardProps) {
  const pathname = usePathname();
  const { isAuthenticated, currentRole, canAccessRoute, navigateToDefault } = useRoleNavigation();

  useEffect(() => {
    // Si no está autenticado, no hacer nada (el auth guard se encargará)
    if (!isAuthenticated) {
      return;
    }

    // Si no tiene rol, redirigir al dashboard
    if (!currentRole) {
      navigateToDefault();
      return;
    }

    // Si se especificaron roles permitidos, verificar que el usuario tenga al menos uno
    if (allowedRoles && allowedRoles.length > 0) {
      const hasAllowedRole = allowedRoles.includes(currentRole);
      if (!hasAllowedRole) {
        navigateToDefault();
        return;
      }
    }

    // Verificar si puede acceder a la ruta actual
    if (!canAccessRoute(pathname)) {
      navigateToDefault();
    }
  }, [isAuthenticated, currentRole, pathname, canAccessRoute, navigateToDefault, allowedRoles]);

  // Si no está autenticado, mostrar fallback o nada
  if (!isAuthenticated) {
    return fallback || null;
  }

  // Si no tiene rol, mostrar fallback o nada
  if (!currentRole) {
    return fallback || null;
  }

  // Si se especificaron roles permitidos y el usuario no tiene ninguno, mostrar fallback
  if (allowedRoles && allowedRoles.length > 0) {
    const hasAllowedRole = allowedRoles.includes(currentRole);
    if (!hasAllowedRole) {
      return fallback || null;
    }
  }

  // Si no puede acceder a la ruta, mostrar fallback
  if (!canAccessRoute(pathname)) {
    return fallback || null;
  }

  // Si todo está bien, mostrar el contenido
  return <>{children}</>;
}

/**
 * Hook para verificar acceso a rutas
 */
export function useRouteAccess() {
  const pathname = usePathname();
  const { isAuthenticated, currentRole, canAccessRoute } = useRoleNavigation();

  return {
    canAccess: isAuthenticated && currentRole ? canAccessRoute(pathname) : false,
    isAuthenticated,
    currentRole,
  };
}

/**
 * Hook para obtener el menú de navegación del usuario
 */
export function useUserNavigationMenu() {
  const { getCurrentUserMenu } = useRoleNavigation();
  return getCurrentUserMenu();
}
